/**
 * Editing a version 2 rule tree: pure functions, no DOM, no fetching.
 *
 * The block editor works on a slightly looser tree than the server stores -
 * the gate and the condition are always a group, even with nothing or one
 * thing in it, so there is always somewhere to click "+ condition". `normalize`
 * turns that back into what `backend/argus_tree/document.py` accepts: empty
 * groups disappear, one-child groups become their child, and inputs and params
 * nobody uses any more are dropped, so an orphan never narrows the rule's scope.
 *
 * Every function returns a new document; nothing is mutated in place, so Lit
 * sees each edit as a change and an abandoned edit leaves the original alone.
 */

import type { ArgusTreeDocument, ArgusTreeNode, ArgusTreeScope } from '../../api/types.ts';

export type Path = Array<string | number>;
type Arg = ArgusTreeNode | string | number;

export const DURATIONS = ['10m', '15m', '30m', '1h', '2h', '6h', '12h', '24h', '3d', '7d', '30d'];
export const WINDOWS = ['1h', '6h', '12h', '24h', '3d', '7d', '30d'];
export const COMPARISONS = ['>', '>=', '<', '<=', '==', '!='] as const;
export const AGGREGATES = ['avg_over', 'min_over', 'max_over', 'p95_over', 'sum_over'] as const;

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function getAt(root: unknown, path: Path): unknown {
  let here: unknown = root;
  for (const key of path) {
    if (here == null) return undefined;
    here = (here as Record<string | number, unknown>)[key];
  }
  return here;
}

/** A copy of `doc` with the value at `path` replaced. */
export function setAt(doc: ArgusTreeDocument, path: Path, value: unknown): ArgusTreeDocument {
  const next = clone(doc);
  const parent = getAt(next, path.slice(0, -1)) as Record<string | number, unknown>;
  parent[path[path.length - 1] as string | number] = value;
  return next;
}

/** A copy with the list item at `path` removed. */
export function removeAt(doc: ArgusTreeDocument, path: Path): ArgusTreeDocument {
  const next = clone(doc);
  const list = getAt(next, path.slice(0, -1)) as unknown[];
  list.splice(Number(path[path.length - 1]), 1);
  return next;
}

/** A copy with `node` appended to the list at `path`. */
export function pushAt(doc: ArgusTreeDocument, path: Path, node: ArgusTreeNode): ArgusTreeDocument {
  const next = clone(doc);
  (getAt(next, path) as unknown[]).push(node);
  return next;
}

/* ── groups ──────────────────────────────────────────────────
   Five ways to combine conditions, one shape on screen. ALL and ANY are
   `and` / `or`; AT LEAST is `at_least` with the count first; NONE is `not`
   over an `or`, because the catalogue's `not` takes one condition - so its
   conditions live one level down. NOT ALL ("not both") is `not` over an `and`,
   its conditions one level down too. `groupItems` hides that from the editor.
   A condition that must be false is NONE of that one condition; the editor
   draws it as the condition in red with NOT, no box (`negated`). Chosen
   2026-10-08 (`docs/design/sketches/argus-rule-groups.html`). */

export type GroupKind = 'all' | 'any' | 'none' | 'not_all' | 'at_least';

const GROUP_OPS = new Set(['and', 'or', 'not', 'at_least']);

function opOf(node: unknown): string | null {
  return typeof node === 'object' && node !== null && 'op' in node ? (node as { op: string }).op : null;
}

function isGroup(node: unknown): node is { op: string; args: Arg[] } {
  return GROUP_OPS.has(opOf(node) ?? '');
}

export function groupKind(node: unknown): GroupKind | null {
  switch (opOf(node)) {
    case 'and':
      return 'all';
    case 'or':
      return 'any';
    case 'not':
      return opOf((node as { args: Arg[] }).args[0]) === 'and' ? 'not_all' : 'none';
    case 'at_least':
      return 'at_least';
    default:
      return null;
  }
}

export interface GroupItems {
  /** The list the conditions are kept in - where "+ condition" appends. */
  list: Path;
  /** Index of the first condition in that list: AT LEAST keeps its count first. */
  offset: number;
  items: Arg[];
  /** AT LEAST's count. */
  k?: number;
}

export function groupItems(node: { op: string; args: Arg[] }, path: Path): GroupItems {
  if (node.op === 'at_least') {
    return { list: [...path, 'args'], offset: 1, items: node.args.slice(1), k: node.args[0] as number };
  }
  if (node.op === 'not') {
    const inner = node.args[0];
    if (opOf(inner) === 'or' || opOf(inner) === 'and') {
      return { list: [...path, 'args', 0, 'args'], offset: 0, items: (inner as { args: Arg[] }).args };
    }
    return { list: [...path, 'args'], offset: 0, items: inner === undefined ? [] : [inner] };
  }
  return { list: [...path, 'args'], offset: 0, items: node.args };
}

function buildGroup(kind: GroupKind, items: Arg[], k?: number): ArgusTreeNode {
  if (kind === 'any') return { op: 'or', args: items } as ArgusTreeNode;
  if (kind === 'none') return { op: 'not', args: [{ op: 'or', args: items }] } as ArgusTreeNode;
  if (kind === 'not_all') return { op: 'not', args: [{ op: 'and', args: items }] } as ArgusTreeNode;
  if (kind === 'at_least') {
    // A new, empty group starts at two - the usual "two of these" - and the
    // Check panel says it is short until two are in it.
    const count = items.length ? Math.max(1, Math.min(k ?? 2, items.length)) : (k ?? 2);
    return { op: 'at_least', args: [count, ...items] } as ArgusTreeNode;
  }
  return { op: 'and', args: items } as ArgusTreeNode;
}

/** The group at `path`, turned into another kind with the same conditions. */
export function setGroupKind(doc: ArgusTreeDocument, path: Path, kind: GroupKind): ArgusTreeDocument {
  const node = getAt(doc, path) as { op: string; args: Arg[] };
  const { items, k } = groupItems(node, path);
  return setAt(doc, path, buildGroup(kind, clone(items), k));
}

/** AT LEAST's count, kept between 1 and the number of conditions it lists. */
export function setGroupCount(doc: ArgusTreeDocument, path: Path, k: number): ArgusTreeDocument {
  const node = getAt(doc, path) as { op: string; args: Arg[] };
  const n = Math.max(1, node.args.length - 1);
  return setAt(doc, [...path, 'args', 0], Math.max(1, Math.min(n, Math.round(k))));
}

/**
 * Remove one item from a list. When the list belongs to an AT LEAST group its
 * count comes down with it - "3 of these" over two conditions is a rule that
 * can never fire, and the person removing one did not ask for that.
 */
export function removeItem(doc: ArgusTreeDocument, path: Path): ArgusTreeDocument {
  let next = removeAt(doc, path);
  const owner = path.slice(0, -2);
  const node = getAt(next, owner) as { op: string; args: Arg[] } | undefined;
  if (opOf(node) === 'at_least' && node) {
    const n = node.args.length - 1;
    if ((node.args[0] as number) > n) next = setAt(next, [...owner, 'args', 0], Math.max(1, n));
  }
  return next;
}

/**
 * A condition that must be false: NONE over that one condition, the
 * condition not a group. Returns the condition and its path below the NONE,
 * or null. Accepts the stored `not(x)` as well as the editor's `not(or([x]))`.
 */
export function negated(node: unknown): { inner: ArgusTreeNode; sub: Path } | null {
  if (opOf(node) !== 'not') return null;
  const inner = (node as { args: Arg[] }).args[0];
  if (opOf(inner) === 'or') {
    const items = (inner as { args: Arg[] }).args;
    if (items.length !== 1 || isGroup(items[0]) || typeof items[0] !== 'object') return null;
    return { inner: items[0] as ArgusTreeNode, sub: ['args', 0, 'args', 0] };
  }
  if (inner === undefined || isGroup(inner) || typeof inner !== 'object') return null;
  return { inner: inner as ArgusTreeNode, sub: ['args', 0] };
}

/** Must be true <-> must be false, for the condition at `path`. */
export function toggleNot(doc: ArgusTreeDocument, path: Path): ArgusTreeDocument {
  const node = getAt(doc, path) as ArgusTreeNode;
  const neg = negated(node);
  if (neg) return setAt(doc, path, clone(neg.inner));
  return setAt(doc, path, { op: 'not', args: [{ op: 'or', args: [clone(node)] }] } as ArgusTreeNode);
}

/**
 * A shorter shape with the same meaning, for the group at `path`'s node -
 * offered as a "Simplify" chip, never applied by itself:
 * - NONE of one ALL group is NOT ALL of its items ("not both");
 * - ALL of conditions that must each be false is NONE of them ("neither");
 * - ANY of conditions that must each be false is NOT ALL of them.
 * `to` is the group kind it becomes, `count` how many items it will hold.
 */
export function simplification(node: unknown): { to: GroupKind; count: number } | null {
  const op = opOf(node);
  if (!op) return null;
  const args = (node as { args: Arg[] }).args;
  if (op === 'not' && opOf(args[0]) === 'or') {
    const items = (args[0] as { args: Arg[] }).args;
    if (items.length === 1 && opOf(items[0]) === 'and') {
      return { to: 'not_all', count: (items[0] as { args: Arg[] }).args.length };
    }
    return null;
  }
  if ((op === 'and' || op === 'or') && args.length > 1 && args.every((a) => negated(a))) {
    return { to: op === 'and' ? 'none' : 'not_all', count: args.length };
  }
  return null;
}

/** Apply `simplification` to the group at `path`. Unchanged when there is none. */
export function simplifyAt(doc: ArgusTreeDocument, path: Path): ArgusTreeDocument {
  const node = getAt(doc, path) as { op: string; args: Arg[] };
  const plan = simplification(node);
  if (!plan) return doc;
  const items =
    node.op === 'not'
      ? ((node.args[0] as { args: Arg[] }).args[0] as { args: Arg[] }).args
      : node.args.map((a) => (negated(a) as { inner: ArgusTreeNode }).inner);
  return setAt(doc, path, buildGroup(plan.to, clone(items)));
}

/** NOT over one condition becomes NOT over a one-item ANY, so it can grow.
 *  NOT over an ALL is NOT ALL, a group already, and stays as it is. */
function loosen(node: Arg): Arg {
  if (typeof node !== 'object' || node === null || !('op' in node)) return node;
  const args = node.args.map(loosen);
  if (node.op === 'not' && opOf(args[0]) !== 'or' && opOf(args[0]) !== 'and') {
    return { op: 'not', args: [{ op: 'or', args: [args[0] as Arg] }] } as ArgusTreeNode;
  }
  return { ...node, args } as ArgusTreeNode;
}

function asGroup(node: ArgusTreeNode | null): ArgusTreeNode {
  if (node == null) return { op: 'and', args: [] };
  const loose = loosen(node) as ArgusTreeNode;
  return isGroup(loose) ? loose : { op: 'and', args: [loose] };
}

/**
 * The gate, moved into the condition as more AND items. The editor shows one
 * Conditions clause (decided 2026-10-07), so a rule with a gate - an older
 * one, or one the Assistant wrote - is read as `condition AND gate`. A silence
 * rule has no condition to join, so its gate stays where it is.
 */
export function foldGate(doc: ArgusTreeDocument): ArgusTreeDocument {
  if (doc.shape.kind === 'silence' || !doc.gate) return doc;
  const items = (node: ArgusTreeNode | null): ArgusTreeNode[] =>
    node == null ? [] : opOf(node) === 'and' ? ((node as { args: ArgusTreeNode[] }).args ?? []) : [node];
  const gate = items(doc.gate);
  if (!gate.length) return { ...doc, gate: null };
  return { ...doc, gate: null, condition: { op: 'and', args: [...items(doc.condition), ...gate] } as ArgusTreeNode };
}

/** A fresh rule: every brand-sized decision left for the person, nothing guessed. */
export function blankDocument(source: string, scope: ArgusTreeScope): ArgusTreeDocument {
  return {
    schema_version: 2,
    source,
    scope,
    inputs: {},
    params: {},
    define: {},
    gate: { op: 'and', args: [] },
    condition: { op: 'and', args: [] },
    shape: { kind: 'sustained', dwell: '1h' },
    severity: 'watch',
  };
}

/** A stored document, loosened so every clause has somewhere to add to. */
export function editable(doc: ArgusTreeDocument): ArgusTreeDocument {
  const next = clone(foldGate(doc));
  next.define = next.define ?? {};
  next.inputs = next.inputs ?? {};
  next.params = next.params ?? {};
  // Only a silence rule keeps a gate of its own; `foldGate` joined any other.
  next.gate = next.shape.kind === 'silence' ? asGroup(next.gate) : null;
  next.condition = asGroup(next.condition);
  // A named condition is a group, as the clauses are, so one plain
  // comparison can grow "+ condition" and turn into ANY or ALL from the
  // screen; `normalize` unwraps a group of one again. Signals are numbers
  // and stay as they are.
  for (const entry of Object.values(next.define)) {
    entry.expr = isNumberNode(entry.expr, next) ? (loosen(entry.expr) as ArgusTreeNode) : asGroup(entry.expr);
  }
  return next;
}

function tighten(node: Arg): Arg | null {
  if (typeof node !== 'object' || node === null || !('op' in node)) return node;
  const args = node.args.map(tighten).filter((a): a is Arg => a !== null);
  if (node.op === 'and' || node.op === 'or') {
    if (args.length === 0) return null;
    if (args.length === 1) return args[0] as Arg;
  }
  // NONE of nothing says nothing; AT LEAST 1 of one condition is that condition.
  if (node.op === 'not' && args.length === 0) return null;
  if (node.op === 'at_least') {
    if (args.length <= 1) return null;
    if (args.length === 2) return args[1] as Arg;
  }
  return { op: node.op, args };
}

function collect(node: unknown, inputs: Set<string>, params: Set<string>, refs: Set<string>): void {
  if (typeof node !== 'object' || node === null) return;
  if ('input' in node) inputs.add((node as { input: string }).input);
  else if ('param' in node) params.add((node as { param: string }).param);
  else if ('ref' in node) refs.add((node as { ref: string }).ref);
  else if ('op' in node) {
    for (const arg of (node as unknown as { args: unknown[] }).args) collect(arg, inputs, params, refs);
  }
}

/** What the server stores: no empty groups, no orphaned inputs or params. */
export function normalize(doc: ArgusTreeDocument): ArgusTreeDocument {
  const next = clone(foldGate(doc));
  next.gate = (next.gate ? tighten(next.gate) : null) as ArgusTreeNode | null;
  next.condition =
    next.shape.kind === 'silence'
      ? null
      : ((next.condition ? tighten(next.condition) : null) as ArgusTreeNode | null);
  for (const entry of Object.values(next.define)) {
    entry.expr = (tighten(entry.expr) ?? entry.expr) as ArgusTreeNode;
  }
  const inputs = new Set<string>();
  const params = new Set<string>();
  const refs = new Set<string>();
  collect(next.gate, inputs, params, refs);
  collect(next.condition, inputs, params, refs);
  for (const entry of Object.values(next.define)) collect(entry.expr, inputs, params, refs);
  if (next.shape.kind === 'silence') inputs.add(next.shape.input);
  next.inputs = Object.fromEntries(Object.entries(next.inputs).filter(([k]) => inputs.has(k)));
  next.params = Object.fromEntries(Object.entries(next.params).filter(([k]) => params.has(k)));
  return next;
}

function freeName(prefix: string, taken: Record<string, unknown>): string {
  let n = 1;
  while (`${prefix}_${n}` in taken) n += 1;
  return `${prefix}_${n}`;
}

/**
 * The input that reads `series`, adding one if none does. Picking a series for
 * one pill never changes what another pill reads - it points this pill at an
 * input of its own, and the orphan is swept by `normalize`.
 */
export function inputFor(doc: ArgusTreeDocument, series: string): [ArgusTreeDocument, string] {
  if (series) {
    for (const [alias, entry] of Object.entries(doc.inputs)) {
      if (entry.series === series && !entry.per_entity) return [doc, alias];
    }
  }
  const next = clone(doc);
  const alias = freeName('in', next.inputs);
  next.inputs[alias] = { series };
  return [next, alias];
}

/** A new threshold of its own. Each number pill owns one, so moving it moves nothing else. */
export function newParam(doc: ArgusTreeDocument, value: number): [ArgusTreeDocument, string] {
  const next = clone(doc);
  const name = freeName('p', next.params);
  next.params[name] = { value };
  return [next, name];
}

export type BlockKind =
  | 'compare'
  | 'between'
  | 'average'
  | 'valid'
  | 'stale'
  | 'all'
  | 'any'
  | 'none'
  | 'at_least';

/** A new block, with a signal still to be chosen where it needs one. */
export function template(doc: ArgusTreeDocument, kind: BlockKind): [ArgusTreeDocument, ArgusTreeNode] {
  if (kind === 'all') return [doc, { op: 'and', args: [] }];
  if (kind === 'any') return [doc, { op: 'or', args: [] }];
  if (kind === 'none' || kind === 'at_least') return [doc, buildGroup(kind, [])];
  let [next, alias] = inputFor(doc, '');
  const input = { input: alias };
  if (kind === 'valid') return [next, { op: 'valid', args: [input] }];
  if (kind === 'stale') return [next, { op: 'stale', args: [input, '6h'] }];
  if (kind === 'between') {
    let low: string;
    let high: string;
    [next, low] = newParam(next, 0);
    [next, high] = newParam(next, 100);
    return [next, { op: 'between', args: [input, { param: low }, { param: high }] }];
  }
  let limit: string;
  [next, limit] = newParam(next, 0);
  const watched: ArgusTreeNode = kind === 'average' ? { op: 'avg_over', args: [input, '24h'] } : input;
  return [next, { op: '>', args: [watched, { param: limit }] }];
}

/** Every named condition that another part of the rule still points at. */
export function refsInUse(doc: ArgusTreeDocument): Set<string> {
  const refs = new Set<string>();
  const sink = new Set<string>();
  collect(doc.gate, sink, sink, refs);
  collect(doc.condition, sink, sink, refs);
  for (const entry of Object.values(doc.define)) collect(entry.expr, sink, sink, refs);
  return refs;
}

/** A new named condition, with a comparison still to be filled in. */
export function addDefine(doc: ArgusTreeDocument, label: string): ArgusTreeDocument {
  let [next, expr] = template(doc, 'compare');
  next = clone(next);
  const key = freeName('named', next.define);
  next.define[key] = { label, expr: asGroup(expr) };
  return next;
}

/**
 * A copy of a named condition, placed right after it, named "<label> copy".
 * Each number pill owns its threshold, so the copy gets thresholds of its own:
 * changing a number in one never changes the other. Signals and other named
 * conditions are shared, as a pill picked anew points at an input of its own.
 */
export function duplicateDefine(doc: ArgusTreeDocument, key: string): [ArgusTreeDocument, string] {
  const entry = doc.define[key];
  if (!entry) return [doc, key];
  const next = clone(doc);
  const copyParams = (node: unknown): unknown => {
    if (typeof node !== 'object' || node === null) return node;
    if ('param' in node) {
      const name = freeName('p', next.params);
      next.params[name] = clone(next.params[(node as { param: string }).param] ?? { value: 0 });
      return { ...node, param: name };
    }
    if ('op' in node) {
      const op = node as unknown as { args: unknown[] };
      return { ...op, args: op.args.map(copyParams) };
    }
    return node;
  };
  const labels = new Set(Object.values(doc.define).map((d) => d.label));
  let label = `${entry.label} copy`;
  for (let n = 2; labels.has(label); n += 1) label = `${entry.label} copy ${n}`;
  const copyKey = freeName('named', next.define);
  const copy = { label, expr: copyParams(clone(entry.expr)) as ArgusTreeNode };
  // Rebuilt in order, so the copy sits right under its original.
  next.define = Object.fromEntries(
    Object.entries(next.define).flatMap(([k, v]) => (k === key ? [[k, v], [copyKey, copy]] : [[k, v]])),
  );
  return [next, copyKey];
}

/** Inputs no signal has been chosen for yet - the first thing a person must fix. */
export function unchosenInputs(doc: ArgusTreeDocument): number {
  return Object.values(normalize(doc).inputs).filter((entry) => !entry.series).length;
}

/* ── signals: named numbers in the Where clause ─────────────
   A `define` is either a condition (true or false) or a signal (a number).
   The server types it; here the answer only decides where it is offered - a
   signal where a series can go, a condition where a condition can. */

const NUMBER_OPS = new Set([
  '+', '-', '*', '/', 'abs', 'min', 'max',
  'avg_over', 'min_over', 'max_over', 'sum_over', 'p95_over', 'stddev_over',
  'change_over', 'missing_over', 'share_over', 'as_number',
]);

export function isNumberNode(node: unknown, doc: ArgusTreeDocument, seen = new Set<string>()): boolean {
  if (typeof node !== 'object' || node === null) return false;
  if ('input' in node || 'param' in node || 'const' in node) return true;
  if ('ref' in node) {
    const key = (node as { ref: string }).ref;
    if (seen.has(key)) return false;
    seen.add(key);
    return isNumberNode(doc.define[key]?.expr, doc, seen);
  }
  return NUMBER_OPS.has(opOf(node) ?? '');
}

/** The signals in the Where clause, in the order they were written. */
export function signalKeys(doc: ArgusTreeDocument): string[] {
  return Object.keys(doc.define).filter((key) => isNumberNode(doc.define[key]?.expr, doc));
}

/** Whether `node` reads the define `key`, directly or through other defines. */
export function usesDefine(node: unknown, doc: ArgusTreeDocument, key: string, seen = new Set<string>()): boolean {
  if (typeof node !== 'object' || node === null) return false;
  if ('ref' in node) {
    const ref = (node as { ref: string }).ref;
    if (ref === key) return true;
    if (seen.has(ref)) return false;
    seen.add(ref);
    return usesDefine(doc.define[ref]?.expr, doc, key, seen);
  }
  return ((node as { args?: unknown[] }).args ?? []).some((arg) => usesDefine(arg, doc, key, seen));
}

/**
 * The names a signal's formula may use, label to key: the other signals, and
 * the named conditions (each counts 1 while it holds, 0 while it does not).
 * A define that already reads the signal `self` is left out - it would define
 * itself.
 */
export function formulaNames(
  doc: ArgusTreeDocument,
  self: string | null,
): { signals: Record<string, string>; conditions: Record<string, string> } {
  const signals: Record<string, string> = {};
  const conditions: Record<string, string> = {};
  for (const [key, entry] of Object.entries(doc.define)) {
    if (key === self || (self !== null && usesDefine(entry.expr, doc, self))) continue;
    (isNumberNode(entry.expr, doc) ? signals : conditions)[entry.label] = key;
  }
  return { signals, conditions };
}

const FORMULA_FUNCS: Record<string, string> = {
  avg_over: 'AVG', min_over: 'MIN', max_over: 'MAX', sum_over: 'SUM', stddev_over: 'STDDEV',
};

const PRECEDENCE: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

function quoted(series: string): string {
  return JSON.stringify(series);
}

/**
 * A signal's tree written back as the formula a person would type - the same
 * dialect Explore's derived signals use. The tree is what is stored; this is
 * only how it is shown, so a signal the Assistant wrote reads the same way as
 * one typed by hand. Null when a part has no formula spelling.
 */
export function formulaOf(node: unknown, doc: ArgusTreeDocument, parent = 0): string | null {
  if (typeof node !== 'object' || node === null) return null;
  if ('input' in node) return quoted(doc.inputs[(node as { input: string }).input]?.series ?? '');
  if ('const' in node) return String((node as { const: number }).const);
  if ('param' in node) {
    const entry = doc.params[(node as { param: string }).param];
    return entry ? String(entry.value) : null;
  }
  // Another signal is named, as Explore's derived signals name each other.
  if ('ref' in node) {
    const entry = doc.define[(node as { ref: string }).ref];
    return entry ? quoted(entry.label) : null;
  }
  const { op, args } = node as { op: string; args: unknown[] };
  // A named condition, counted 1 or 0, is written as its name.
  if (op === 'as_number') return 'ref' in (args[0] as object) ? formulaOf(args[0], doc) : null;
  if (op in FORMULA_FUNCS) {
    const inner = formulaOf(args[0], doc);
    return inner === null ? null : `${FORMULA_FUNCS[op]}(${inner}, ${String(args[1])})`;
  }
  const level = PRECEDENCE[op];
  if (level) {
    // 0 - x is how a leading minus is stored.
    if (op === '-' && (args[0] as { const?: number })?.const === 0) {
      const inner = formulaOf(args[1], doc, 3);
      return inner === null ? null : `-${inner}`;
    }
    const left = formulaOf(args[0], doc, level);
    const right = formulaOf(args[1], doc, level + (op === '-' || op === '/' ? 1 : 0));
    if (left === null || right === null) return null;
    const text = `${left} ${op} ${right}`;
    return level < parent ? `(${text})` : text;
  }
  return null;
}

const TEXT_COMPARE: Record<string, string> = {
  '>': 'above', '>=': 'at least', '<': 'below', '<=': 'at most', '==': 'equals', '!=': 'is not',
};

const TEXT_WINDOW: Record<string, string> = {
  avg_over: 'average', min_over: 'lowest', max_over: 'highest', sum_over: 'total',
  p95_over: '95th percentile', stddev_over: 'spread', change_over: 'change',
  missing_over: 'missing readings', share_over: '% of time',
};

function valueText(node: unknown, doc: ArgusTreeDocument): string {
  if (typeof node !== 'object' || node === null) return '…';
  if ('input' in node) return doc.inputs[(node as { input: string }).input]?.series || 'a signal';
  if ('param' in node) {
    const entry = doc.params[(node as { param: string }).param];
    return entry ? `${entry.value}${entry.unit ? ` ${entry.unit}` : ''}` : '…';
  }
  if ('const' in node) return String((node as { const: number }).const);
  if ('ref' in node) return doc.define[(node as { ref: string }).ref]?.label ?? '…';
  const { op, args } = node as { op: string; args: unknown[] };
  if (op in TEXT_WINDOW) return `${String(args[1])} ${TEXT_WINDOW[op]} of ${valueText(args[0], doc)}`;
  return formulaOf(node, doc) ?? '…';
}

/**
 * A condition as one short line, for a menu: "DG1_ACTIVE_POWER above 250".
 * A group of one reads as its one condition; a larger group by its kind and
 * size, "ALL of 3". Not the server's sentence - only enough to recognise it.
 */
export function conditionText(node: unknown, doc: ArgusTreeDocument): string {
  if (typeof node !== 'object' || node === null) return '';
  if ('ref' in node) return valueText(node, doc);
  const { op, args } = node as { op: string; args: unknown[] };
  if (op in TEXT_COMPARE) return `${valueText(args[0], doc)} ${TEXT_COMPARE[op]} ${valueText(args[1], doc)}`;
  if (op === 'between' || op === 'outside') {
    return `${valueText(args[0], doc)} ${op} ${valueText(args[1], doc)} and ${valueText(args[2], doc)}`;
  }
  if (op === 'valid') return `${valueText(args[0], doc)} has a reading`;
  if (op === 'stale') return `${valueText(args[0], doc)} quiet for ${String(args[1])}`;
  const kind = groupKind(node);
  if (kind) {
    const { items, k } = groupItems(node as { op: string; args: Arg[] }, []);
    if (items.length === 1 && (kind === 'all' || kind === 'any')) return conditionText(items[0], doc);
    const head = { all: 'ALL', any: 'ANY', none: 'NONE', not_all: 'NOT ALL', at_least: `AT LEAST ${k ?? ''}` }[kind];
    return items.length ? `${head} of ${items.length}` : `${head} of nothing yet`;
  }
  return op;
}

/** A copy with a signal set: a new one when `key` is null. Returns the key. */
export function putSignal(
  doc: ArgusTreeDocument,
  key: string | null,
  label: string,
  expr: ArgusTreeNode,
  inputs: ArgusTreeDocument['inputs'],
): [ArgusTreeDocument, string] {
  const next = clone(doc);
  next.inputs = clone(inputs);
  const name = key ?? freeName('signal', next.define);
  next.define[name] = { label, expr };
  return [next, name];
}
